import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
} from '@fluentui/react-components';

type ConfirmVariant = 'danger' | 'primary';

export interface ConfirmOptions {
  title?: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  variant?: ConfirmVariant;
}

interface ConfirmState extends Required<ConfirmOptions> {
  isOpen: boolean;
}

type ConfirmFn = (options: ConfirmOptions) => Promise<boolean>;

const ConfirmContext = createContext<ConfirmFn | null>(null);

const defaultState: ConfirmState = {
  isOpen: false,
  title: 'Confirm action',
  message: '',
  confirmText: 'Confirm',
  cancelText: 'Cancel',
  variant: 'danger'
};

const ConfirmDialog: React.FC<{
  state: ConfirmState;
  onConfirm: () => void;
  onCancel: () => void;
}> = ({ state, onConfirm, onCancel }) => {
  return (
    <Dialog
      modalType="modal"
      open={state.isOpen}
      onOpenChange={(_, data) => {
        if (!data.open) {
          onCancel();
        }
      }}
    >
      <DialogSurface className="confirm-dialog-surface">
        <DialogBody>
          <DialogTitle>{state.title}</DialogTitle>
          <DialogContent className="confirm-dialog-body">
            {state.message}
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={onCancel}>
              {state.cancelText}
            </Button>
            <Button
              appearance="primary"
              className={state.variant === 'danger' ? 'fluent-danger-button' : undefined}
              onClick={onConfirm}
            >
              {state.confirmText}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};

export const ConfirmProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, setState] = useState<ConfirmState>(defaultState);
  const resolverRef = useRef<((value: boolean) => void) | null>(null);

  const close = useCallback(() => {
    setState(prev => ({ ...prev, isOpen: false }));
  }, []);

  const confirm = useCallback<ConfirmFn>((options) => {
    if (resolverRef.current) {
      resolverRef.current(false);
      resolverRef.current = null;
    }

    setState({
      isOpen: true,
      title: options.title ?? defaultState.title,
      message: options.message,
      confirmText: options.confirmText ?? defaultState.confirmText,
      cancelText: options.cancelText ?? defaultState.cancelText,
      variant: options.variant ?? defaultState.variant
    });

    return new Promise((resolve) => {
      resolverRef.current = resolve;
    });
  }, []);

  const handleConfirm = useCallback(() => {
    if (resolverRef.current) {
      resolverRef.current(true);
      resolverRef.current = null;
    }
    close();
  }, [close]);

  const handleCancel = useCallback(() => {
    if (resolverRef.current) {
      resolverRef.current(false);
      resolverRef.current = null;
    }
    close();
  }, [close]);

  const contextValue = useMemo(() => confirm, [confirm]);

  return (
    <ConfirmContext.Provider value={contextValue}>
      {children}
      <ConfirmDialog state={state} onConfirm={handleConfirm} onCancel={handleCancel} />
    </ConfirmContext.Provider>
  );
};

export const useConfirm = (): ConfirmFn => {
  const ctx = useContext(ConfirmContext);
  if (!ctx) {
    throw new Error('useConfirm must be used within ConfirmProvider');
  }
  return ctx;
};
